import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
    Observable, BehaviorSubject, distinctUntilChanged, filter, map, Subject, finalize, switchMap, combineLatest, zip, of, take
} from 'rxjs';

import { DateTime } from 'luxon';

import { SharedSettings } from '../models/shared-settings';
import { url } from '../helpers/url';

import { EntityActionType } from '../models/entity-action-type';
import { ShiftStatus, WorkedShift } from '../models/worked-shift';
import { ValidationSummary } from '../models/validation-summary';
import { LockStatus } from '../models/lock-status';
import { HubService } from '../authentication/hub.service';
import { LockState } from '../models/lock-state';
import { arraysMatch } from '../helpers/arrays-match';
import { LoggingService } from './logging.service';
import { ShiftPrecheckResult } from '../models/shift-precheck-result';
import { Site } from '../models/site';

interface ShiftListener {
    subject: Subject<WorkedShift[]>;
    condition: (ws: WorkedShift) => boolean;
};

@Injectable({
    providedIn: 'root'
})
export class WorkedShiftsService {

    private _connectionStatus = new BehaviorSubject<'connected' | 'disconnected' | 'reconnecting'>('disconnected');

    /// <summary>
    /// SignalR hub connection status; shown as a tooltip on the copyright for debugging purposes.
    /// </summary>
    public get ConnectionStatus(): Observable<'connected' | 'disconnected' | 'reconnecting'>
    {
        return this._connectionStatus.asObservable();
    }

    private dateLocks: BehaviorSubject<LockStatus[]> = new BehaviorSubject<LockStatus[]>([]);
    private workedShifts: BehaviorSubject<WorkedShift[]> = new BehaviorSubject<WorkedShift[]>([]);

    private activeListeners: ShiftListener[] = [];

    constructor(
        private settings: SharedSettings,
        private http: HttpClient,
        private hubService: HubService,
        private logger: LoggingService
    ) {
        // update followers
        combineLatest([this.workedShifts, this.dateLocks]).subscribe(([workedShifts, dateLocks]) => {
            const withLocks: WorkedShift[] = workedShifts.map(ws => {
                const lock = dateLocks.find(l => l.siteId === ws.siteId && ws.date.equals(l.date));
                return {
                    id: ws.id,
                    siteId: ws.siteId,
                    date: ws.date,
                    shiftId: ws.shiftId,
                    shift: ws.shift,
                    shiftStatus: lock?.lockState === LockState.Locked ? ShiftStatus.Locked : ws.shiftStatus,
                    documentIds: ws.documentIds
                };
            });

            this.activeListeners.forEach(al => {
                al.subject.next(withLocks.filter(s => al.condition(s)));
            });
        });

        // track locks for tracked shifts
        this.workedShifts.pipe(
            filter(workedShifts => workedShifts?.length > 0),
            distinctUntilChanged((prev, cur) => arraysMatch(prev, cur, (a, b) => a.id === b.id)),
            switchMap(workedShifts => {
                const join = (ws: WorkedShift, ls: LockStatus): boolean => ws.siteId === ls.siteId && ws.date.equals(ls.date);

                const existingLocks = this.dateLocks.value.filter(l => workedShifts.some(s => join(s, l)));
                const missingLocks = workedShifts.filter(ws => !existingLocks.some(l => join(ws, l)));

                const getRanges = missingLocks.reduce((acc, cur) => {
                    if (acc[cur.siteId]) {
                        acc[cur.siteId].min = DateTime.min(acc[cur.siteId].min, cur.date);
                        acc[cur.siteId].max = DateTime.max(acc[cur.siteId].max, cur.date);
                    } else {
                        acc[cur.siteId] = { min: cur.date, max: cur.date };
                    }
                    return acc;
                }, {} as { [siteId: Site['id']]: { min: DateTime; max: DateTime } });

                return zip([
                    of(existingLocks),
                    ...Object.entries(getRanges)
                        .map(([siteId, range]) => this.http
                            .get<LockStatus[]>(this.settings.apiBaseUrl
                                + url`WorkedShifts/locks/${siteId}/dates?start=${range.min.toISODate()}&end=${range.max.toISODate()}`)
                            .pipe(map(locks => locks.map(l => {
                                l.date = DateTime.fromISO(<any>l.date);
                                return l;
                            }))))
                ]);
            })
        ).subscribe(([...locks]) => {

            const dedupedlocks = locks.flat(1).reverse().reduce((acc, cur) => {
                if (!acc.some(l => l.id === cur.id))
                    acc.push(cur);
                return acc;
            }, [] as LockStatus[]);

            this.dateLocks.next(dedupedlocks);
        });

        const connection = this.hubService.connect('ShiftStatus');

        connection.on('UpdateWorkedShift', (workedShift: WorkedShift, action: EntityActionType) => {
            workedShift.date = DateTime.fromISO(<any>workedShift.date);

            this.updateShifts(
                action === EntityActionType.Remove ? [] : [workedShift],
                ws => ws.id === workedShift.id);
        });

        connection.on('UpdateSiteLock', (lockStatus: LockStatus, action: EntityActionType) => {
            lockStatus.date = DateTime.fromISO(<any>lockStatus.date);

            this.dateLocks.next([
                ...this.dateLocks.value.filter(wls => wls.id !== lockStatus.id),
                ...(action === EntityActionType.Remove ? [] :  [lockStatus])
            ]);
        });

        connection.onreconnecting(() => this._connectionStatus.next('reconnecting'));
        connection.onreconnected(() => this._connectionStatus.next('connected'));
        connection.onclose(() => this._connectionStatus.next('disconnected'));

        connection.start()
            .then(() => this._connectionStatus.next('connected'))
            .catch(err => this.logger.error(err));
    }

    private createListener(condition: (shift: WorkedShift) => boolean): Observable<WorkedShift[]> {
        const listener = {
            subject: new Subject<WorkedShift[]>(),
            condition
        };

        this.activeListeners.push(listener);

        return listener.subject.pipe(
            finalize(() => {
                this.activeListeners.splice(this.activeListeners.indexOf(listener), 1);
            }),
            distinctUntilChanged((prev, cur) => arraysMatch(prev, cur, (a, b) => this.workedShiftsMatch(a, b))));
    }

    private updateShifts(updatedShifts: WorkedShift[], remCondition: (shift: WorkedShift) => boolean): void {
        const currentShifts = this.workedShifts.value;

        const allShifts = [
            ...currentShifts.filter(s => !remCondition(s)),
            ...updatedShifts
        ];

        const followedShifts = allShifts.filter(s => this.activeListeners.some(l => l.condition(s)));

        this.workedShifts.next(followedShifts);
    }

    private workedShiftsMatch(x: WorkedShift, y: WorkedShift): boolean {
        return x.id === y.id
            && x.siteId === y.siteId
            && x.date.equals(y.date)
            && x.shiftId === y.shiftId
            && x.shiftStatus === y.shiftStatus;
    }

    FollowById(id: number): Observable<WorkedShift> {
        this.UpdateById(id);

        return this.createListener(s => s.id === id).pipe(
            filter(ws => !!ws.length),
            map(ws => ws[0]));
    }

    FollowByCalendarMonth(siteId: string, month: number, year: number): Observable<WorkedShift[]> {
        this.UpdateByMonth(siteId, month, year);

        return this.createListener(s => s.siteId === siteId
            && s.date.month === month
            && s.date.year === year);
    }

    FollowByDate(siteId: string, date: DateTime): Observable<WorkedShift[]> {
        this.UpdateByDate(siteId, date);

        return this.createListener(s => s.siteId === siteId
            && s.date.equals(date));
    }

    FollowByDateRange(siteId: string, startDate: DateTime, endDate: DateTime): Observable<WorkedShift[]> {
        this.UpdateByDateRange(siteId, startDate, endDate);

        return this.createListener(s => s.siteId === siteId
            && s.date >= startDate
            && s.date <= endDate);
    }

    PeekById(id: number): Observable<WorkedShift | null> {
        return this.workedShifts.pipe(
            map(workedShifts => workedShifts.find(ws => ws.id === id)),
            filter(ws => !!ws),
            take(1));
    }

    private UpdateById(id: number): void {
        if (!id)
        {
            if (!this.settings.isProduction)
                throw new Error('WorkedShiftsService.UpdateById: id was null!');
            return;
        }

        this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts/${id}`)
            .subscribe(updated => {
                if (!!updated) {
                    updated.date = DateTime.fromISO(<any>updated.date);

                    this.updateShifts([updated], ws => ws.id === id);
                }
                else {
                    console.warn(`WorkedShiftsService.UpdateById received NULL from server for id ${id}`);
                }
            });
    }

    private UpdateByMonth(siteId: string, month: number, year: number): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl + url`WorkedShifts/dates?siteId=${siteId}&month=${month}&year=${year}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws =>
                    ws.siteId === siteId &&
                    ws.date.month === month &&
                    ws.date.year === year);
            });
    }

    private UpdateByDate(siteId: string, date: DateTime): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl + url`WorkedShifts/search?siteId=${siteId}&date=${date.toISODate()}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws =>
                    ws.siteId === siteId &&
                    ws.date.equals(date));
            });
    }

    private UpdateByDateRange(siteId: string, startDate: DateTime, endDate: DateTime): void {
        this.http.get<WorkedShift[]>(this.settings.apiBaseUrl
            + url`WorkedShifts/search?siteId=${siteId}&startDate=${startDate.toISODate()}&endDate=${endDate.toISODate()}`)
            .subscribe(updated => {
                updated.forEach(ws => ws.date = DateTime.fromISO(<any>ws.date));

                this.updateShifts(updated, ws => ws.siteId === siteId
                    && ws.date >= startDate
                    && ws.date <= endDate);
            });
    }

    GetByDateShift(siteId: string, date: DateTime, shiftId: number): Observable<WorkedShift | null> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl
            + url`WorkedShifts?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`)
            .pipe(map(workedShift => {
                if (!workedShift)
                    return null;
                workedShift.date = DateTime.fromISO(<any>workedShift.date);

                this.updateShifts([workedShift], ws => ws.id === workedShift.id);

                return workedShift;
            }));
    }
    GetLatestForSite(siteId: string): Observable<WorkedShift> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts?siteId=${siteId}`)
            .pipe(map(workedShift => {
                if (!!workedShift) {
                    workedShift.date = DateTime.fromISO(<any>workedShift.date);

                    this.updateShifts([workedShift], ws => ws.id === workedShift.id);
                }
                return workedShift;
        }));
    }

    GetImmediateById(id: number): Observable<WorkedShift> {
        return this.http.get<WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts/${id}`)
            .pipe(map(workedShift => {
                workedShift.date = DateTime.fromISO(<any>workedShift.date);

                this.updateShifts([workedShift], ws => ws.id === workedShift.id);

                return workedShift;
            }));
    }

    GetShiftPrecheck(siteId: string, date: DateTime, shiftId: number): Observable<ShiftPrecheckResult> {
        return this.http.get<ShiftPrecheckResult>(this.settings.apiBaseUrl
            + url`WorkedShifts/precheck?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`);
    }


    GetLockStatusImmediate(workedShiftId: number): Observable<LockStatus> {
        return this.http.get<LockStatus>(this.settings.apiBaseUrl + url`WorkedShifts/${workedShiftId}/lock`)
            .pipe(map(lockStatus => {
                lockStatus.date = DateTime.fromISO(<any>lockStatus.date);

                this.dateLocks.next([
                    ...this.dateLocks.value.filter(wls => wls.id !== lockStatus.id),
                    lockStatus
                ]);
                return lockStatus;
            }));
    }


    OpenShift(siteId: string, date: DateTime, shiftId: number): Observable<WorkedShift> {
        return this.http.post<WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts`, {
            siteId,
            date: date.toISODate(),
            shiftId
        }).pipe(map(updated => {
            updated.date = DateTime.fromISO(<any>updated.date);

            this.updateShifts([updated], ws => ws.id === updated.id);

            return updated;
        }));
    }

    SetWorkedShiftStatus(id: number, status: ShiftStatus): Observable<WorkedShift> {
        return this.http.patch<WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts/${id}`, status)
            .pipe(map(updated => {
                updated.date = DateTime.fromISO(<any>updated.date);

                this.updateShifts([updated], ws => ws.id === updated.id);

                return updated;
            }));
    }

    DeleteWorkedShift(id: number, siteId: string): Observable<ValidationSummary | WorkedShift> {
        return this.http.delete<ValidationSummary | WorkedShift>(this.settings.apiBaseUrl + url`WorkedShifts/${id}?siteId=${siteId}`)
            .pipe(map(response => {
                // if response is WorkedShift, and thus success
                if (!response.hasOwnProperty('errors')) {
                    (<WorkedShift>response).date = DateTime.fromISO(<any>(<WorkedShift>response).date);

                    this.updateShifts([], ws => ws.id === id);
                }
                return response;
            }));
    }
}

